/** UUID, immer vom Client erzeugt (Regel 2 in CLAUDE.md). */
export type Uuid = string;

/** ISO-8601 mit Zeitzone, zum Beispiel 2026-09-19T08:14:03.271Z. */
export type IsoTimestamp = string;

/**
 * Pflichtfelder jeder synchronisierten Zeile.
 *
 * - `updated_at` setzt die Datenbank per Trigger. Der Sync-Pull nutzt es als Cursor.
 * - `client_updated_at` setzt das Gerät. Es entscheidet Konflikte, weil Geräte
 *   offline schreiben und die Serverzeit dann nichts über die Reihenfolge sagt.
 * - `deleted_at` ist der Grabstein. Es wird nie hart gelöscht, sonst kommen
 *   gelöschte Zeilen über den Sync eines anderen Geräts zurück.
 */
export interface SyncedRow {
  id: Uuid;
  updated_at: IsoTimestamp;
  client_updated_at: IsoTimestamp;
  deleted_at: IsoTimestamp | null;
}

/**
 * Jede Tabelle, die einer Familie gehört, trägt zusätzlich `family_id`
 * (Regel 2). Die einzige Ausnahme ist `families` selbst: sie **ist** die
 * Familie und hat deshalb keine Spalte, die auf eine zeigt.
 */
export interface FamilyScopedRow extends SyncedRow {
  family_id: Uuid;
}

/**
 * Tabellen, die über die Outbox synchronisiert werden.
 *
 * `families` ist dabei ein Sonderfall: gelesen wird sie wie alles andere,
 * geschrieben nur über die Operation "update". Neue Familien entstehen
 * ausschließlich über create_family().
 */
export const SYNCED_TABLES = [
  "families",
  "members",
  "invites",
  "stores",
  "lists",
  "items",
  "products",
  "trips",
  "trip_items",
  "price_entries",
  "recipes",
  "recipe_ingredients",
  "pantry_items",
  "meal_plan_entries",
  "partner_connections",
] as const;

export type SyncedTable = (typeof SYNCED_TABLES)[number];

export function isSyncedTable(value: string): value is SyncedTable {
  return (SYNCED_TABLES as readonly string[]).includes(value);
}
